import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { type AppLanguageCode, type AppLanguageOption } from '../models/app-language.model';

interface TranslationTree {
  [key: string]: string | TranslationTree;
}

@Injectable({ providedIn: 'root' })
export class TranslationService {
  private readonly storageKey = 'expert-language';

  private readonly translations = signal<TranslationTree>({});
  readonly language = signal<AppLanguageCode>('en');

  readonly languageOptions: readonly AppLanguageOption[] = [
    { value: 'cs', label: 'CZ', flag: '🇨🇿' },
    { value: 'en', label: 'EN', flag: '🇬🇧' }
  ];

  constructor(private readonly http: HttpClient) {}

  async init(): Promise<void> {
    const storedLanguage = this.readStoredLanguage();
    if (storedLanguage) {
      this.language.set(storedLanguage);
    }

    await this.loadLanguage(this.language());
  }

  async setLanguage(language: AppLanguageCode): Promise<void> {
    if (language === this.language()) {
      return;
    }

    await this.loadLanguage(language);
    this.language.set(language);
    localStorage.setItem(this.storageKey, language);
  }

  translate(key: string): string {
    const value = key
      .split('.')
      .reduce<string | TranslationTree | undefined>((current, part) => {
        if (!current || typeof current === 'string') {
          return current;
        }

        return current[part];
      }, this.translations());

    return typeof value === 'string' ? value : key;
  }

  private async loadLanguage(language: AppLanguageCode): Promise<void> {
    const candidatePaths = [
      `i18n/${language}.json`,
      `assets/i18n/${language}.json`
    ];

    for (const path of candidatePaths) {
      try {
        const dictionary = await firstValueFrom(this.http.get<TranslationTree>(path));
        this.translations.set(dictionary);
        return;
      } catch {
        continue;
      }
    }

    this.translations.set({});
  }

  private readStoredLanguage(): AppLanguageCode | null {
    const value = localStorage.getItem(this.storageKey);
    return value === 'en' || value === 'cs' ? value : null;
  }
}
